// BriefAudioPlayer.jsx — the FULL player UI on the brief detail page.
//
// Playback itself is owned by audioPlayerStore — a singleton that keeps the
// <audio> element alive across tab switches and even when the app is
// backgrounded (iOS background audio is configured in Info.plist +
// AppDelegate). This component is a thin UI layer that reads store state
// and commands it.
//
// Responsibilities:
//   - On Listen tap, fetch the MP3 via /api/tts, cache the blob URL, and
//     hand it to the store to play. We keep a module-level Map so a second
//     play within the same session is instant.
//   - Render a full player (play/pause, 15s seek, scrubber, speed cycle)
//     while the brief detail page is visible.
//   - NOT own the audio element. The element lives in audioPlayerStore so
//     it survives navigating away — that's what makes the "keeps playing"
//     behavior possible.

import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Icon } from './Icon.jsx';
import { ProcessingOrb } from './ProcessingOrb.jsx';
import { fetchAudio, narrationFromBrief } from '../lib/briefApi.js';
import { AIConsentDeclinedError } from '../lib/aiConsent.js';
import { probeMp3Duration } from '../lib/mp3Duration.js';
import { injectXingHeader } from '../lib/injectXingHeader.js';
import { sfx } from '../lib/sfx.js';
import * as audioStore from '../lib/audioPlayerStore.js';

// Module-level cache so replaying within the same session does not re-fetch.
// Key: briefId. Value: { blobUrl, generatedAt, preciseDuration }.
//
// `preciseDuration` is computed by walking the MP3 frames at fetch time —
// see mp3Duration.js for the why. Caching it alongside the blob URL means
// re-attachments (user backs out and returns) still get the right total.
const audioCache = new Map();

const SPEEDS = [1, 1.25, 1.5, 2];

function fmt(t) {
  if (!isFinite(t) || t < 0) return '0:00';
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

// React subscription hook for the audio store.
function useAudioStore() {
  return useSyncExternalStore(audioStore.subscribe, audioStore.getState, audioStore.getState);
}

export function BriefAudioPlayer({ brief, voiceId, initialSpeed = 1, onSpeedChange, onAudioReady }) {
  const scrubRef = useRef(null);
  const [minimized, setMinimized] = useState(false);
  const s = useAudioStore();

  // Is the store currently showing THIS brief?
  const isMine = s.brief?.id && brief?.id && s.brief.id === brief.id;
  const phase = isMine ? s.status : 'idle';
  const playing = isMine && s.playing;
  const t = isMine ? s.t : 0;
  const dur = isMine ? s.dur : 0;
  const speed = isMine ? s.speed : initialSpeed;

  // Local error copy only when this brief is in error state in the store.
  const errorMsg = isMine ? s.error : '';

  // If the brief we're viewing already has a cached blob, attach it to the
  // store in 'ready' state so the full player UI shows without autoplay.
  useEffect(() => {
    if (!brief?.id) return;
    if (isMine) return; // already attached
    const hit = audioCache.get(brief.id);
    if (hit?.blobUrl) {
      audioStore.attachExisting({
        brief,
        blobUrl: hit.blobUrl,
        speed: initialSpeed,
        preciseDuration: hit.preciseDuration || 0,
      });
    }
  }, [brief?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async () => {
    if (!brief) return;
    audioStore.markLoading(brief);
    sfx.startNarrating && sfx.startNarrating();
    try {
      const text = narrationFromBrief(brief);
      const originalBlob = await fetchAudio({ text, voiceId });

      // Two things happen here, both aimed at WKWebView's broken VBR
      // MP3 handling:
      //
      //  1. Probe the real duration off the raw bytes by walking every
      //     MPEG frame (see mp3Duration.js). This used to be our only
      //     defense — it gave the scrubber a correct length even when
      //     WebKit's own estimate was wildly off.
      //
      //  2. Inject a synthetic Xing info header into the byte stream
      //     BEFORE handing it to WebKit (see injectXingHeader.js). This
      //     is the real fix: it makes WebKit's own `audio.duration`
      //     come out correctly, which in turn makes `el.currentTime = X`
      //     honored for any X inside the file. Without it, the spec's
      //     "clamp currentTime to duration" rule turns any mid-drag
      //     scrub past WebKit's underestimate into an instant `ended`,
      //     which is what made the scrubber hard-cap at 3:50 on a 6:48
      //     brief.
      //
      //  (1) is kept as belt-and-braces in case injection fails — e.g.
      //  if the TTS vendor ever changes encoders to something our
      //  parser doesn't cover.
      let playableBlob = originalBlob;
      let preciseDuration = 0;
      try {
        const buf = await originalBlob.arrayBuffer();
        preciseDuration = probeMp3Duration(buf);
        const rewritten = injectXingHeader(buf);
        // Only replace the blob if injection actually added something —
        // otherwise injectXingHeader returned the input untouched (already
        // had a VBR header, or parse bailed).
        if (rewritten.length !== buf.byteLength) {
          playableBlob = new Blob([rewritten], { type: originalBlob.type || 'audio/mpeg' });
        }
      } catch {
        /* probe / inject are best-effort; fall back to the original blob
           + element-reported duration if anything here throws. */
      }
      const url = URL.createObjectURL(playableBlob);
      audioCache.set(brief.id, {
        blobUrl: url,
        generatedAt: new Date().toISOString(),
        preciseDuration,
      });
      await audioStore.loadAndPlay({ brief, blobUrl: url, speed: initialSpeed, preciseDuration });
      if (typeof onAudioReady === 'function') onAudioReady({ url });
    } catch (e) {
      // User declined the AI consent prompt — clear the loading state
      // and stay on the idle "Listen" CTA without throwing an error.
      if (e instanceof AIConsentDeclinedError) {
        audioStore.stop();
        return;
      }
      audioStore.markError(e?.message || 'Could not generate audio. Try again.');
    } finally {
      sfx.stopNarrating && sfx.stopNarrating();
    }
  };

  const togglePlay = () => {
    if (phase === 'idle' || !isMine) {
      sfx.toast && sfx.toast();
      generate();
      return;
    }
    audioStore.toggle();
  };

  const skip = (delta) => audioStore.skip(delta);

  const cycleSpeed = () => {
    const i = SPEEDS.indexOf(speed);
    const next = SPEEDS[(i + 1) % SPEEDS.length];
    audioStore.setSpeed(next);
    if (typeof onSpeedChange === 'function') onSpeedChange(next);
  };

  const onScrubChange = (e) => {
    if (!dur) return;
    const pct = Number(e.target.value) / 100;
    audioStore.seek(pct * dur);
  };

  // Idle state: render a primary "Listen" CTA, NOT the sticky bar.
  if (phase === 'idle') {
    return (
      <button
        onClick={generate}
        className="btn btn-primary"
        style={{
          width: '100%', padding: '14px 18px', borderRadius: 14, fontSize: 14,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10,
        }}
      >
        <Icon name="radio" size={16} color="#fff"/>
        Listen to this briefing
      </button>
    );
  }

  if (phase === 'loading') {
    return (
      <div style={stickyBarStyle()}>
        <div style={{
          padding: '14px 14px 16px',
          display: 'flex', justifyContent: 'center',
        }}>
          <ProcessingOrb
            size={56}
            label="Generating narration"
            sublabel="Warming up the voice for today's brief."
          />
        </div>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div style={stickyBarStyle()}>
        <div style={{ ...rowStyle(), gap: 10 }}>
          <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: '#FF8A9E', flex: 1 }}>
            {errorMsg || 'Audio failed. Try again.'}
          </span>
          <button className="btn btn-ghost" onClick={generate} style={{ padding: '8px 12px', fontSize: 12 }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Ready.
  const pct = dur > 0 ? Math.min(100, Math.max(0, (t / dur) * 100)) : 0;

  return (
    <div style={stickyBarStyle()}>
      {minimized ? (
        <div style={{ ...rowStyle(), gap: 10 }}>
          <button
            onClick={togglePlay}
            aria-label={playing ? 'Pause' : 'Play'}
            style={playBtnStyle(34)}
          >
            <Icon name={playing ? 'pause' : 'play'} size={14} color="#fff"/>
          </button>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 11.5, color: 'var(--fg-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              Now playing · {brief?.title || 'The Briefing'}
            </div>
            <div style={{ height: 3, borderRadius: 999, background: 'rgba(255,255,255,0.08)', marginTop: 4, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${pct}%`, background: 'var(--grad-cta)' }}/>
            </div>
          </div>
          <button onClick={() => setMinimized(false)} className="btn-icon" title="Expand">
            <Icon name="chevron-d" size={14}/>
          </button>
        </div>
      ) : (
        <div style={{ padding: '12px 14px 14px' }}>
          {/* Top row: title + collapse */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <div className="eyebrow" style={{ fontSize: 9, color: 'var(--accent)' }}>Now playing</div>
            <div style={{ flex: 1 }}/>
            <button onClick={() => setMinimized(true)} className="btn-icon" title="Minimize">
              <Icon name="chevron-d" size={14}/>
            </button>
          </div>

          {/* Scrubber */}
          <input
            ref={scrubRef}
            type="range" min="0" max="100" step="0.1"
            value={pct}
            onChange={onScrubChange}
            style={{
              width: '100%',
              accentColor: 'var(--accent)',
              height: 4,
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontFamily: 'var(--font-body)', fontSize: 10, color: 'var(--fg-4)' }}>
            <span>{fmt(t)}</span>
            <span>{fmt(dur)}</span>
          </div>

          {/* Transport row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 18, marginTop: 10 }}>
            <button onClick={cycleSpeed} style={speedBtnStyle()}>
              {speed}x
            </button>
            <button onClick={() => skip(-15)} className="btn-icon" title="Back 15s">
              <Icon name="rewind-15" size={18}/>
            </button>
            <button onClick={togglePlay} aria-label={playing ? 'Pause' : 'Play'} style={playBtnStyle(54)}>
              <Icon name={playing ? 'pause' : 'play'} size={22} color="#fff"/>
            </button>
            <button onClick={() => skip(15)} className="btn-icon" title="Forward 15s">
              <Icon name="forward-15" size={18}/>
            </button>
            <div style={{ width: 44 }}/>
          </div>
        </div>
      )}
    </div>
  );
}

function stickyBarStyle() {
  return {
    position: 'sticky',
    bottom: 76, // clear the tab bar
    left: 0, right: 0,
    margin: '0 10px',
    borderRadius: 20,
    background: 'radial-gradient(circle at 100% 0%, rgba(45,156,255,0.14), transparent 60%), rgba(19,21,25,0.94)',
    backdropFilter: 'blur(30px) saturate(180%)',
    border: '1px solid var(--stroke-1)',
    boxShadow: '0 -12px 40px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.06)',
    zIndex: 50,
  };
}

function rowStyle() {
  return { padding: '12px 14px', display: 'flex', alignItems: 'center' };
}

function playBtnStyle(size) {
  return {
    width: size, height: size, borderRadius: size / 2,
    background: 'var(--grad-cta)',
    border: 'none',
    display: 'grid', placeItems: 'center',
    boxShadow: '0 0 18px rgba(45,156,255,0.45), inset 0 1px 0 rgba(255,255,255,0.3)',
    cursor: 'pointer',
  };
}

function speedBtnStyle() {
  return {
    minWidth: 44, padding: '6px 10px', borderRadius: 999,
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid var(--stroke-2)',
    color: 'var(--fg-2)',
    fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 11,
    cursor: 'pointer',
  };
}
